import {
  ChangeDetectorRef,
  Directive,
  ElementRef,
  OnDestroy,
  OnInit,
  QueryList,
  Renderer2,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import {
  ContentControlledEditingDirectiveViewModel,
  SegmentExtender,
  SegmentExtenderKeyframe,
} from 'app/js/src/domain/content-control/editing-directive';
import {
  ContentControlledEventActionEnum,
  ContentControlledMediaTypeEnum,
} from 'app/js/src/domain/content-control/enums';
import { IContentControlStudioTimeline } from './i-content-control-studio-timeline';
import { ContentControlStudioBaseViewModel } from '../content-control-studio-base-view-model';
import { VideoTimeFrameMetadata } from './content-control-studio-video-metadata';
import { ETimelineMode } from 'app/js/src/domain/content-control/studio/e-timeline-mode';

/** Payload for keyframe click. */
export class KeyframeClickPayload {
  constructor(
    public segmentId: number,
    public timeInMs: number,
  ) {}
}

/** Single tick in a timeline block. */
export class TimeBlockTick {
  constructor(
    public xPx: number,
    public y1Px: number,
    public y2Px: number,
    public widthPx: number,
  ) {}
}

/** One time block in the timeline ruler. */
export class TimeBlock {
  public static readonly TICKS_NUMBER = 10;
  constructor(
    public ticks: TimeBlockTick[],
    public widthPx: number,
    public timestampMs: number,
    public timestampLeftPx?: number,
  ) {}
}

/** Position for a tooltip element. */
export class TooltipPosition {
  constructor(
    public x: number,
    public y: number,
  ) {}
}

/**
 * Abstract base directive for VOD and Live timeline components.
 *
 * Owns shared timeline state (drag flags, playhead position, segment arrays,
 * tooltip) and shared behaviour (segment filtering, overlap detection,
 * mark-in/out dragging, time formatting, track resizing).
 *
 * Subclasses must supply a {@link vm ViewModel}, the authoritative
 * {@link _editingDirectives} list, and every abstract method that
 * differs between VOD (video-relative time) and Live (UTC-based time).
 */
@Directive()
export abstract class ContentControlStudioBaseTimelineComponent
  implements IContentControlStudioTimeline, OnInit, OnDestroy
{
  // -------------------------------------------------------------------------
  // Abstract members
  // -------------------------------------------------------------------------

  /** The studio ViewModel that drives this timeline's data and commands. */
  public abstract get vm(): ContentControlStudioBaseViewModel;

  /** The full list of editing directives currently held by the ViewModel. */
  protected abstract get _editingDirectives(): Array<ContentControlledEditingDirectiveViewModel>;

  // -------------------------------------------------------------------------
  // Abstract methods — differ fundamentally between VOD and Live
  // -------------------------------------------------------------------------

  /**
   * Returns the visible timeline duration in seconds.
   *
   * In normal mode this is the full video/buffer duration. In fit-segment mode
   * it returns the active segment's duration plus margins. In Edited Time (ET)
   * mode it subtracts the total duration of all cut segments.
   * @param isFitSegment - Whether the timeline is zoomed to fit the active segment.
   * @param isEditedTimeline - Whether cut segments are collapsed (ET mode).
   */
  public abstract currentVideoTimeSecs(isFitSegment: boolean, isEditedTimeline: boolean): number;

  /** Returns the current playback position in seconds (player-relative). */
  public abstract currentVideoPlayTimeSecs(): number;

  /**
   * Returns the playback timestamp for UI display.
   * VOD: playback time in milliseconds. Live: HLS playlist-end as a UTC timestamp.
   */
  public abstract currentVideoPlayTimeForUI(): number;


  /** Returns the current pixel density (pixels per one second of video time). */
  public abstract pixelsPerSecond(): number;

  /**
   * Seeks the underlying video source to the given time.
   * @param time - Target time in seconds, relative to the video/player origin.
   */
  protected abstract _videoSeekToTime(time: number): void;

  /**
   * Public entry point for seeking the video player.
   * Delegates to the subclass-specific {@link _videoSeekToTime}.
   * @param timeSecs - Target time in seconds, relative to the video/player origin.
   */
  public seekToVideoPlayerRelativeTimeInSecs(timeSecs: number): void {
    this._videoSeekToTime(timeSecs);
  }

  /**
   * Finds the cut-action editing directive that overlaps the given time.
   * @param timeToCheckSecs - Time to test, in seconds.
   * @returns The overlapping cut directive, or `undefined` if none.
   */
  public abstract getCutSegmentAtSpecificTime(timeToCheckSecs: number): ContentControlledEditingDirectiveViewModel | undefined;

  /**
   * Returns a frame-snapped seek time one frame past the end of a cut segment.
   * Used to skip past a cut boundary when playback reaches it.
   * @param cutEndMs - The cut segment's end time in milliseconds.
   */
  public abstract getSeekTimeAfterCutEnd(cutEndMs: number): number;

  /**
   * Seeks the video to the active segment's in-point or out-point.
   * @param point - `'in'` for the segment start, `'out'` for the segment end.
   */
  public abstract goToSelectedEventTime(point: 'in' | 'out'): void;

  /**
   * Scrolls the timeline viewport so the playhead remains visible.
   * @param type - `'afterZoom'` centres on the playhead after a zoom change;
   *   `'next'` follows the current time forward; `'previous'` scrolls one
   *   viewport width backward.
   */
  public abstract translateTimeline(type?: 'afterZoom' | 'next' | 'previous'): void;

  /** Called when the video source reports that metadata (duration, fps) is available. */
  public abstract onVideoMetadataLoaded(): void;

  /** Updates the tooltip position and formatted time string on mouse movement over the timeline. */
  public abstract onMouseMove(event: MouseEvent): void;

  /**
   * Rebuilds the visual {@link SegmentExtender} arrays from editing directives.
   * Distributes segments into per-media-type arrays and recalculates pixel
   * positions and widths.
   * @param segments - Optional override list; when omitted the current
   *   {@link _editingDirectives} are used.
   */
  public abstract rebuildSegmentsInternally(segments?: Array<ContentControlledEditingDirectiveViewModel>): void;

  /**
   * Positions the playhead in response to a timeline click or touch.
   * When called without an event, repositions the playhead at the current
   * video time (e.g. after a zoom change).
   */
  protected abstract _onTimelineClickedEventHandler(event?: MouseEvent | TouchEvent): void;

  /** Recalculates all timeline geometry: video metadata, time blocks, block widths, and track sizes. */
  protected abstract _calculateVideoTimeline(): void;

  /**
   * Global mousemove handler (attached to `window`). Delegates to the
   * subclass-specific drag logic for timeline panning, playhead dragging,
   * and mark-in/out dragging.
   */
  protected abstract _onWindowMouseMove(e: MouseEvent): void;

  /**
   * Records the initial mouse position and begins a timeline-drag when not
   * in fit-to-video mode. Prevents default to avoid text selection during drag.
   */
  public onTimelineMouseDown(event: MouseEvent | TouchEvent): void {
    this._timelineMouseDownPos = this._timelineStartPosPx = (event as MouseEvent).clientX;
    if (this.isVideoTimelineFit === false) {
      this._isTimelineDragging = true;
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }

  /**
   * Ends all active drag operations (timeline pan, playhead, mark-in/out).
   *
   * If the timeline was not actually dragged (mouse-down position unchanged),
   * treats the interaction as a click and seeks the playhead via
   * {@link _onTimelineClickedEventHandler}. Notifies the view model when a segment
   * mark was dragged so it can persist the change.
   * @param event - The mouseup event, used for click-seek detection.
   */
  public clearDrags(event?: MouseEvent): void {
    if (this._timelineMouseDownPos === this._timelineStartPosPx && event) {
      this._isTimelineDragging = false;
      this._timelineMouseDownPos = -1;
      this._onTimelineClickedEventHandler(event);
    }
    if (this.isVideoTimelineFit === false && this._isTimelineDragging) {
      this._isTimelineDragging = false;
    }
    if (this._isPlayheadDragging) {
      this._isPlayheadDragging = false;
      this._playheadPreviousLeft = this._playheadCurrentLeft;
    }
    if (this._isCurrentSegmentInMarkDragging || this._isCurrentSegmentOutMarkDragging) {
      const active = this.getActiveSegment();
      if (active) {
        this.vm.activeSegmentChangedOnTimeline(active.item);
      }
    }
    this._isCurrentSegmentInMarkDragging = false;
    this._isCurrentSegmentOutMarkDragging = false;
  }

  /**
   * Selects a segment by ID. Delegates to the view model only if the segment is
   * not already selected, or if the active segment is an edit-subtitle event
   * (which requires re-selection to refresh the subtitle editor).
   * @param id - The segment ID to select.
   */
  public changeSelectedSegment(id: number): void {
    const active = this.getActiveSegment();
    if (
      this.selectedSegmentId !== id ||
      (active && active.item.IsEditSubtitleEvent)
    ) {
      this.vm.changeSelectedSegment(id);
    }
  }

  /** Recalculates the pixel width of a single ruler block from the block duration and current pixel density. */
  protected _calculateTimelineBlockWidth(): void {
    this._timelineBlockWidthPx = this.timelineBlockDurationSecs * this.pixelsPerSecond();
  }

  // -------------------------------------------------------------------------
  // VM-derived properties — proxy ViewModel state for template binding
  // -------------------------------------------------------------------------

  /** Duration of a single ruler block in seconds (default 600 = 10 min). */
  public get timelineBlockDurationSecs(): number { return this.vm?.selectedTimelineBlockDuration ?? 600; }
  /** Whether the timeline is zoomed to fit the entire video duration. */
  public get isVideoTimelineFit(): boolean { return this.vm?.isVideoTimelineFit ?? true; }
  /** Whether the timeline is zoomed to fit the active segment. */
  public get isFitSegment(): boolean { return this.vm?.isFitSegment ?? false; }
  /** Whether the timeline is in Edited Time mode (cut segments collapsed). */
  public get isEditedTimeline(): boolean { return this._isET(); }
  /** ID of the currently selected segment, or `undefined` if none. */
  public get selectedSegmentId(): number | undefined { return this.vm?.selectedSegmentId; }

  // -------------------------------------------------------------------------
  // Protected fields
  // -------------------------------------------------------------------------

  /*
   * Segment arrays — rebuilt by `rebuildSegmentsInternally()`.
   * `_internalSegments` is the master list; the rest are media-type subsets
   * consumed by the template's per-track rendering.
   */
  protected _internalSegments: Array<SegmentExtender> = [];
  protected _inclusiveSegments: Array<SegmentExtender> = [];
  protected _audioSegments: Array<SegmentExtender> = [];
  protected _graphicsSegments: Array<SegmentExtender> = [];
  protected _eventNotificationSegments: Array<SegmentExtender> = [];
  protected _videoSegments: Array<SegmentExtender> = [];
  protected _subtitlesSegments: Array<SegmentExtender> = [];

  /* Timeline geometry */
  protected _timeBlocks: TimeBlock[] = [];
  protected _timelineBlocksLengthPx = 0;
  protected _timelineBlockWidthPx = 0;
  protected _componentHeight = 0;
  protected _playheadLineHeight = 0;
  /** Timestamp of the last `requestAnimationFrame` callback (ms), used for frame-rate throttling. */
  protected _lastFrameTime = 0;
  /** Fraction of visible duration added as margin on each side in fit-segment mode. */
  protected _fitSegmentMarginDivisor = 6;
  /** Time window metadata for the current video, recalculated by `_calculateVideoTimeline()`. */
  protected _currentEditingVideoTimeFrame!: VideoTimeFrameMetadata;

  /* Timeline dragging state */
  protected _isTimelineDragging = false;
  protected _timelineStartPosPx = 0;
  /** clientX recorded on mousedown; compared on mouseup to distinguish click from drag. */
  protected _timelineMouseDownPos = -1;
  protected _isPlayheadDragging = false;

  /* Playhead positioning (pixels) */
  protected _playheadCurrentLeft = 0;
  protected _playheadHalfWidth = 6.5;
  protected _playheadStartPos = 0;
  protected _playheadPreviousLeft = 0;

  /* Tooltip state */
  protected _showTooltip = false;
  protected _tooltipPosition = new TooltipPosition(0, 0);
  protected _tooltipCurrentTime = 0;
  protected _tooltipFormattedCurrentTime = '00:00:00.000';

  /* Mark-in / mark-out drag state */
  protected _isCurrentSegmentInMarkDragging = false;
  protected _isCurrentSegmentOutMarkDragging = false;
  protected _currentSegmentInMarkStartPos = 0;
  protected _currentSegmentOutMarkStartPos = 0;

  /** Bound reference to the window-level mousemove listener for clean removal in `ngOnDestroy`. */
  protected _boundMousemoveHandler: ((e: MouseEvent) => void) | null = null;

  // -------------------------------------------------------------------------
  // Public fields
  // -------------------------------------------------------------------------

  /** The outer timeline container element. */
  @ViewChild('timeline') public Timeline!: ElementRef<HTMLDivElement>;
  /** CDK virtual-scroll viewport that hosts the ruler time blocks. */
  @ViewChild('timelineVirtualScroll') public TimelineVirtualScroll!: CdkVirtualScrollViewport;
  /** The playhead indicator element (vertical line + handle). */
  @ViewChild('playhead') public Playhead!: ElementRef<HTMLDivElement>;
  /** The dedicated track lane for the currently active (selected) segment. */
  @ViewChild('activeSegmentTrack') public ActiveSegmentTrack!: ElementRef<HTMLDivElement>;
  /** Container wrapping all media-type track lanes. */
  @ViewChild('tracks') public TracksContainer!: ElementRef<HTMLDivElement>;
  /** Individual track lane elements (one per media type). */
  @ViewChildren('track') public Tracks!: QueryList<ElementRef>;

  public readonly isNaN = isNaN;
  public readonly ContentControlledMediaTypeEnum = ContentControlledMediaTypeEnum;
  public oneFrameSecs = 0;
  public videoFps = 0;

  // -------------------------------------------------------------------------
  // Constructor
  // -------------------------------------------------------------------------

  constructor(
    protected _renderer: Renderer2,
    protected _cd: ChangeDetectorRef,
  ) {}

  // -------------------------------------------------------------------------
  // Lifecycle hooks
  // -------------------------------------------------------------------------

  public ngOnInit(): void {
    this._boundMousemoveHandler = (e: MouseEvent) => this._onWindowMouseMove(e);
    window.addEventListener('mousemove', this._boundMousemoveHandler);
  }

  public ngOnDestroy(): void {
    if (this._boundMousemoveHandler) {
      window.removeEventListener('mousemove', this._boundMousemoveHandler);
      this._boundMousemoveHandler = null;
    }
  }

  // -------------------------------------------------------------------------
  // Event handlers
  // -------------------------------------------------------------------------

  /**
   * Recalculates timeline layout and repositions the playhead.
   * Called after any change that affects geometry (zoom level, timeline mode, block duration).
   * @param re_calculateTimelineBlockWidth - When `true`, recomputes the ruler
   *   block width before recalculating the full timeline.
   */
  public onChangeEffectingTimeline(re_calculateTimelineBlockWidth: boolean = false): void {
    re_calculateTimelineBlockWidth && this._calculateTimelineBlockWidth();
    this._calculateVideoTimeline();
    this._onTimelineClickedEventHandler();
  }

  /** Begins a playhead drag. Prevents propagation so the timeline pan handler is not triggered. */
  public onPlayheadMouseDown(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this._isPlayheadDragging = true;
    this._playheadStartPos = this._getMouseEventPosition(event as MouseEvent);
  }

  /** Begins dragging the active segment's in-point (start) mark. */
  public onInMarkMouseDown(event: Event): void {
    this._isCurrentSegmentInMarkDragging = true;
    this._currentSegmentInMarkStartPos = this._getMouseEventPosition(event as MouseEvent);
  }

  /** Begins dragging the active segment's out-point (end) mark. */
  public onOutMarkMouseDown(event: Event): void {
    this._isCurrentSegmentOutMarkDragging = true;
    this._currentSegmentOutMarkStartPos = this._getMouseEventPosition(event as MouseEvent);
  }

  /** Hides the tooltip and resets its state when the cursor leaves the timeline area. */
  public onMouseLeave(): void {
    this._showTooltip = false;
    this._tooltipCurrentTime = 0;
    this._tooltipFormattedCurrentTime = '00:00:00.000';
    this._tooltipPosition = new TooltipPosition(0, 0);
  }

  /**
   * Handles a click on a keyframe marker inside a segment.
   *
   * If the keyframe belongs to a different segment than the active one, that
   * segment is selected. Otherwise the video is paused and seeked to the
   * keyframe's time.
   * @param payload - Click payload containing the segment ID and keyframe time.
   */
  public onKeyframeClicked(payload: KeyframeClickPayload | object): void {
    const keyframeClickPayload = payload as KeyframeClickPayload;
    const segmentId = keyframeClickPayload.segmentId;
    if (segmentId == null) return;
    const active = this.getActiveSegment();
    if (!active) return;
    if (active.item.id !== segmentId) {
      this.changeSelectedSegment(segmentId);
    } else {
      this.vm.pause();
      this._videoSeekToTime((keyframeClickPayload.timeInMs ?? 0) / 1000);
    }
  }

  /** Suppresses the default action and stops propagation — used on elements that must swallow events. */
  public emptyEventAction(event: Event): void {
    event.preventDefault();
    event.stopImmediatePropagation();
  }

  // -------------------------------------------------------------------------
  // Public methods
  // -------------------------------------------------------------------------

  /**
   * Returns the {@link SegmentExtender} for the currently selected segment.
   *
   * Looks up `_internalSegments` first (already-built visual segments).
   * If not found there (e.g. the segment was just created and not yet rebuilt),
   * falls back to wrapping the raw editing directive in a minimal extender.
   * @returns The active segment extender, or `null` if nothing is selected.
   */
  public getActiveSegment(): SegmentExtender | null {
    const selected = this._internalSegments.find(
      (x) => x.item.id === this.selectedSegmentId,
    );
    if (selected) return selected;

    const directive = this._editingDirectives.find(
      (x) => this.selectedSegmentId === x.id,
    );
    if (!directive) return null;
    return new SegmentExtender(directive.id, directive, null, null, null, null, null);
  }

  public getInclusiveSegments(): SegmentExtender[] { return this._inclusiveSegments; }
  public getEventNotificationSegments(): SegmentExtender[] { return this._eventNotificationSegments; }
  public getVideoSegments(): SegmentExtender[] { return this._videoSegments; }
  public getAudioSegments(): SegmentExtender[] { return this._audioSegments; }
  public getSubtitlesSegments(): SegmentExtender[] { return this._subtitlesSegments; }
  public getGraphicsSegments(): SegmentExtender[] { return this._graphicsSegments; }
  public getTimeBlocks(): TimeBlock[] { return this._timeBlocks; }
  public getTimelineBlockWidthPx(): number { return this._timelineBlockWidthPx; }
  public getComponentHeight(): number { return this._componentHeight; }
  public getShowTooltip(): boolean { return this._showTooltip; }
  public getTooltipPosition(): TooltipPosition { return this._tooltipPosition; }
  public getTooltipFormattedCurrentTime(): string { return this._tooltipFormattedCurrentTime; }
  public getPlayheadLineHeight(): number { return this._playheadLineHeight; }

  /** Visible timeline duration in milliseconds (delegates to {@link currentVideoTimeSecs}). */
  public currentVideoTimeInMs(): number {
    return this.currentVideoTimeSecs(this.isFitSegment, this._isET()) * 1000;
  }

  /** Current playback position in milliseconds (delegates to {@link currentVideoPlayTimeSecs}). */
  public currentVideoPlayTimeInMs(): number {
    return this.currentVideoPlayTimeSecs() * 1000;
  }

  /**
   * Finds all cut-action editing directives that overlap a given time range.
   *
   * Two segments overlap when either one starts inside the other, checked
   * via the pair of conditions `(a.start <= b.start && a.end >= b.start)`.
   * @param timeIn  - Range start in milliseconds.
   * @param timeOut - Range end in milliseconds.
   * @param segmentId - Optional segment ID to exclude from results (typically the segment being edited).
   */
  public getOverlappingCutSegmentsInDuration(
    timeIn: number,
    timeOut: number,
    segmentId?: number,
  ): ContentControlledEditingDirectiveViewModel[] {
    return this._editingDirectives.filter((x) => {
      if (x.action !== ContentControlledEventActionEnum.Cut) return false;
      if (segmentId && segmentId == x.id) return false;
      const one = x.startsAtMs <= timeIn;
      const two = x.endsAtMs >= timeIn;
      const three = timeIn <= x.startsAtMs;
      const four = timeOut >= x.startsAtMs;
      return (one && two) || (three && four);
    });
  }

  /**
   * Navigates to the next or previous keyframe within the active segment.
   *
   * Filters the active segment's keyframes to those marked as actual keyframes,
   * then finds the nearest one in the requested direction relative to the
   * current frame-snapped playback time. Pauses playback and seeks to the
   * keyframe's time.
   * @param position - `'next'` to advance, `'previous'` to go back.
   */
  public goToKeyframe(position: 'next' | 'previous'): void {
    const active = this.getActiveSegment();
    if (!active) return;
    const keyframes = active.keyframes.filter((x: SegmentExtenderKeyframe) => x.isKeyframe);
    if (keyframes.length === 0) return;
    const videoSource = this.vm.getVideoSource();
    const currentTime = videoSource.currentPlayTimeAsClosestFrameMs();
    let nextKeyFrame: SegmentExtenderKeyframe | null;

    if (position === 'next') {
      nextKeyFrame = keyframes.find((x: SegmentExtenderKeyframe) => x.timeInMs > currentTime) ?? null;
    } else {
      nextKeyFrame = keyframes.findLast((x: SegmentExtenderKeyframe) => x.timeInMs < currentTime) ?? null;
    }
    if (nextKeyFrame) {
      this.vm.pause();
      this._videoSeekToTime(videoSource.currentPlayTimeAsClosestFrameSecs(nextKeyFrame.timeInMs / 1000));
    }
  }

  /**
   * Formats a segment timestamp for display, adjusting for Edited Time mode.
   *
   * In ET mode, subtracts the total duration of all cut segments that start
   * before the given time (plus one frame per cut to account for the frame
   * consumed by the cut boundary), producing the "as-aired" timestamp.
   * @param time - The raw timestamp in milliseconds.
   * @returns A formatted time string (e.g. `'01:23:45.678'`).
   */
  public formatCurrentSegmentTime(time: number): string {
    if (this._isET()) {
      const cutsBefore = this._editingDirectives.filter(
        (s) =>
          s.action === ContentControlledEventActionEnum.Cut &&
          s.startsAtMs < time,
      );
      if (cutsBefore && cutsBefore.length > 0) {
        const cutsBeforeDuration = cutsBefore
          .map((cb) => cb.durationMs)
          .reduce((a, b) => a + b);
        time -= cutsBeforeDuration + cutsBefore.length * this.oneFrameSecs * 1000;
      }
    }
    return this._formatTimeSafe(time);
  }

  public trackBySegment(_index: number, segment: SegmentExtender): number {
    return segment.id;
  }

  public trackByTimeBlock(index: number, _timeBlock: TimeBlock): number {
    return index;
  }

  public trackByTickIndex(index: number): number {
    return index;
  }

  /**
   * Initialises frame-rate-dependent values for this timeline.
   * Must be called once after the video source reports its metadata.
   * @param videoFps - The video's frame rate (frames per second).
   */
  public setupTimeline(videoFps: number): void {
    this.videoFps = videoFps;
    this.oneFrameSecs = +(1 / videoFps).toPrecision(12);
  }

  /**
   * Measures the timeline's parent container and sizes the playhead line to
   * span the full track height. Also records `_componentHeight` for the
   * inclusive-segment track.
   */
  public setPlayheadHeight(): void {
    const timelineEl = this.Timeline?.nativeElement;
    const playheadEl = this.Playhead?.nativeElement;
    if (!timelineEl?.parentElement || !playheadEl) return;
    const timelineParentHeight = timelineEl.parentElement.offsetHeight;
    this._playheadLineHeight = timelineParentHeight - 11;
    this._renderer.setStyle(playheadEl, 'display', 'flex');
    this._componentHeight = timelineParentHeight;
  }

  // -------------------------------------------------------------------------
  // Protected methods
  // -------------------------------------------------------------------------

  /** Whether any fit mode is active (fit-to-video or fit-to-segment). */
  protected _isFitted(): boolean {
    return this.isFitSegment || this.isVideoTimelineFit;
  }

  /** Whether the timeline is currently in Edited Time (ET) mode, which collapses cut segments. */
  protected _isET(): boolean {
    return this.vm.timelineMode === ETimelineMode.EditedTime;
  }

  /** Returns the subset of `_internalSegments` whose action is {@link ContentControlledEventActionEnum.Cut}. */
  protected _getCutSegments(): SegmentExtender[] {
    return this._internalSegments.filter(
      (x) => x.item.action === ContentControlledEventActionEnum.Cut,
    ) || [];
  }

  /**
   * Filters segments by media type.
   *
   * In Original Time mode, cut-action segments with `Inclusive` media type are
   * also included regardless of the requested filter, so that cut regions
   * appear on every track.
   * @param filter - The media type to match.
   * @param arr    - Segment array to filter; defaults to `_internalSegments`.
   */
  protected _filterSegments(
    filter: ContentControlledMediaTypeEnum,
    arr?: Array<SegmentExtender>,
  ): SegmentExtender[] {
    arr = arr ?? this._internalSegments;
    return [
      ...arr.filter(
        (x) =>
          x.item.mediaType === filter ||
          (!this._isET() &&
            x.item.mediaType === ContentControlledMediaTypeEnum.Inclusive &&
            x.item.action === ContentControlledEventActionEnum.Cut),
      ),
    ];
  }

  /**
   * Stacks overlapping segments vertically within a single track lane.
   *
   * For each segment, finds all others that overlap it in time, then assigns
   * CSS `height` and `top` so they tile evenly. In ET mode, each segment's
   * `left` is also shifted to account for collapsed cut durations. In
   * fit-segment mode, non-active segments are rendered at reduced opacity.
   * @param segments - The segment array to style (mutated in place).
   */
  protected _setSimilarSegmentsStyle(segments: SegmentExtender[]): void {
    const findOverlappingSegments = (
      segment: SegmentExtender,
      segmentExtenders: Array<SegmentExtender>,
    ) => {
      return segmentExtenders.filter((segmentExtender) => {
        const one = segmentExtender.item.startsAtMs <= segment.item.startsAtMs;
        const two = segmentExtender.item.endsAtMs >= segment.item.startsAtMs;
        const three = segment.item.startsAtMs <= segmentExtender.item.startsAtMs;
        const four = segment.item.endsAtMs >= segmentExtender.item.startsAtMs;
        return (one && two) || (three && four);
      });
    };

    segments.forEach((segment) => {
      if (this._isET() && this.isFitSegment === false) {
        const timeToShiftMs = this._getCutEventsDurationInMsBeforeSpecificTime(
          segment.item.startsAtMs,
        );
        if (timeToShiftMs > 0) {
          segment.left =
            ((segment.item.startsAtMs - timeToShiftMs) / 1000) * this.pixelsPerSecond();
        }
      }
      const overlaps = findOverlappingSegments(segment, segments);
      segment.style = {};
      if (overlaps && overlaps.length > 0) {
        overlaps.sort((a, b) => a.item.startsAtMs - b.item.startsAtMs);
        const index = overlaps.findIndex((x) => x.item.id === segment.item.id);
        const length = overlaps.length;
        const active = this.getActiveSegment();
        segment.style = {
          height: `${100 / length}%`,
          top: `${(100 / length) * index}%`,
          opacity:
            this.isFitSegment && active && segment.item.id !== active.item.id
              ? '0.4'
              : '1',
        };
      }
    });
  }

  /**
   * Rebuilds the inclusive-segment array for template rendering.
   *
   * Filters to {@link ContentControlledMediaTypeEnum.Inclusive}, excludes the
   * currently selected segment (rendered on its own dedicated track), and in
   * Original Time mode also excludes cut-action segments (they are shown
   * inline on every media-type track instead).
   * @param arr - Source array; defaults to `_internalSegments`.
   */
  protected _updateInclusiveSegments(arr?: SegmentExtender[]): void {
    arr = arr ?? this._internalSegments;
    this._inclusiveSegments = this._filterSegments(
      ContentControlledMediaTypeEnum.Inclusive,
      arr,
    ).filter((x) => x.item.id !== this.selectedSegmentId);
    if (!this._isET()) {
      this._inclusiveSegments = this._inclusiveSegments.filter(
        (x) => x.item.action !== ContentControlledEventActionEnum.Cut,
      );
    }
  }

  /** Returns the total pixel width of all time blocks (i.e. the full scrollable ruler width). */
  public getTimelineBlocksLength(): number {
    return this._timeBlocks.reduce((sum, b) => sum + b.widthPx, 0);
  }

  /** Sets the CSS width of the track container and active-segment track to match the full ruler width. */
  protected _resizeTracks(): void {
    const totalWidthPx = this.getTimelineBlocksLength() + this._playheadHalfWidth;
    const tracksEl = this.TracksContainer?.nativeElement;
    if (tracksEl) {
      this._renderer.setStyle(tracksEl, 'width', `${totalWidthPx}px`);
    }
    const activeTrackEl = this.ActiveSegmentTrack?.nativeElement;
    if (activeTrackEl) {
      this._renderer.setStyle(activeTrackEl, 'width', `${totalWidthPx}px`);
    }
  }

  /**
   * Applies a horizontal CSS `translateX` to all track lanes and the
   * active-segment track, scrolling them left by the given pixel amount.
   * @param translateXPx - Pixel offset to scroll (0 = no scroll).
   */
  protected _translateTracks(translateXPx: number): void {
    this.Tracks?.forEach((t) => {
      this._renderer.setStyle(t.nativeElement, 'transform', `translateX(-${translateXPx}px)`);
    });
    const activeTrack = this.ActiveSegmentTrack?.nativeElement;
    if (activeTrack) {
      this._renderer.setStyle(activeTrack, 'transform', `translateX(-${translateXPx}px)`);
    }
  }

  /**
   * Extracts the horizontal page/client coordinate from a mouse or touch event.
   * @returns `pageX` for mouse events, first touch's `clientX` for touch events.
   */
  protected _getMouseEventPosition(event: MouseEvent | TouchEvent | Event): number {
    if (event.type.includes('mouse')) {
      return (event as MouseEvent).pageX ?? (event as MouseEvent).clientX;
    }
    return (event as TouchEvent).touches?.[0]?.clientX ?? 0;
  }

  /**
   * Sums the total duration (ms) of all cut segments that start before the
   * given time.
   *
   * For a cut that only partially precedes `timeInMs` (i.e. its end is past
   * the threshold), only the portion up to `timeInMs` is counted. This makes
   * the result suitable for shifting a segment's visual position leftward in
   * ET mode.
   * @param timeInMs - The reference time in milliseconds.
   */
  protected _getCutEventsDurationInMsBeforeSpecificTime(timeInMs: number): number {
    let result = 0;
    const cutSegs = this._getCutSegments();
    if (cutSegs.length > 0) {
      const cutsBeforeSegment = cutSegs.filter(
        (x) => x.item.startsAtMs < timeInMs,
      );
      if (cutsBeforeSegment.length > 0) {
        result = cutsBeforeSegment
          .map((x) => Math.min(x.item.endsAtMs, timeInMs) - x.item.startsAtMs)
          .reduce((partialSum, a) => partialSum + a, 0);
      }
    }
    return result;
  }

  /**
   * Iteratively computes the accumulated cut duration before a given ET time.
   *
   * Unlike {@link _getCutEventsDurationInMsBeforeSpecificTime}, this method
   * accounts for cascading shifts: removing a cut reveals more original time,
   * which may expose additional cuts. The loop continues until the set of
   * preceding cuts stabilises.
   *
   * Used when converting an ET (edited-time) position back to original time.
   * @param timeInMs - The edited-time position in milliseconds.
   * @returns The total original-time milliseconds consumed by cuts before this point.
   */
  protected _getAccumulatedCutEventsDurationInMsBeforeSpecificTime(timeInMs: number): number {
    let result = 0;
    const cutSegs = this._getCutSegments();
    if (cutSegs.length > 0) {
      let cutsBeforeSegment: SegmentExtender[] = [];
      let cutsBeforeSegmentLength = 0;
      do {
        cutsBeforeSegmentLength = cutsBeforeSegment.length;
        result = cutsBeforeSegment
          .map((x) => x.item.endsAtMs - x.item.startsAtMs)
          .reduce((partialSum, a) => partialSum + a, 0);
        cutsBeforeSegment = cutSegs.filter(
          (x) => x.item.startsAtMs < result + timeInMs,
        );
      } while (cutsBeforeSegment.length !== cutsBeforeSegmentLength);
    }
    return result;
  }

  /**
   * Formats milliseconds into a display string via the ViewModel's format function.
   * Returns `'00:00:00.000'` if the ViewModel or its formatter is not yet available.
   * @param milliseconds - Time value to format.
   * @param justMinutesAndSeconds - When `true`, omits the hours component.
   */
  protected _formatTimeSafe(
    milliseconds: number,
    justMinutesAndSeconds?: boolean,
  ): string {
    return (
      this.vm?.timeFormatFunction?.(milliseconds, justMinutesAndSeconds) ?? '00:00:00.000'
    );
  }

  /**
   * Converts a pixel distance to a time duration in seconds at the current zoom level.
   * Returns `0` when `pixelsPerSecond()` is zero (e.g. before the timeline is initialised).
   */
  protected _pixelsToSecondsConvert(pixels: number): number {
    const pps = this.pixelsPerSecond();
    return pps > 0 ? pixels / pps : 0;
  }
}
